/**
 * Permanent comparison guides (handover "AI-Search-Flagship" v02, Sep 2026).
 *
 * Guides are maintained resource pages, NOT blog posts: no dated URL, no blog
 * feed, no BlogPosting schema. /guides is the index; each guide lives at
 * /guides/{slug} and renders through the shared <GuidePage> template.
 *
 * Copy is the exact approved wording from the v03 package (PROVIDER-GUIDE-COPY.md,
 * FEES-GUIDE-COPY.md) — the
 * visible text must not be edited here without Gary's sign-off. Markup:
 *   [label](/path)        internal link (client-side <Link>)
 *   [label](https://…)    external source link (new tab)
 *   **text**              bold
 *
 * Each guide carries a stable `guideId` used by the guide_link_click and
 * origin_guide_id tracking contract (comparison-guides-v1). Never change a
 * published guideId — reports are keyed on it.
 *
 * Section `position` values are the low-cardinality cta_position parameter;
 * keep them stable once a guide is live.
 */

export const GUIDES_HUB = {
  path: "/guides",
  // TODO(Gary): hub title/intro/meta are template copy written for the
  // implementation, not part of the approved package — confirm before launch.
  seoTitle: "Payment Guides",
  metaDescription:
    "Practical, maintained guides to help UK businesses compare card payment providers, costs and setups before choosing.",
  eyebrow: "Guides",
  h1: "Payment guides",
  intro:
    "Practical guides to help you compare payment providers, costs and setups. We keep each guide under review and note when it was last checked.",
  cardCta: "Read the guide",
};

const CARD_PAYMENT_PROVIDERS_UK = {
  slug: "best-card-payment-providers-uk",
  guideId: "card-payment-providers-uk",
  path: "/guides/best-card-payment-providers-uk",
  eyebrow: "Comparison guide",
  h1: "Best card payment providers for UK small businesses in 2026",
  // Approved metadata: PAGE-METADATA.json (AI-Search-Guides-20260924-v03).
  seoTitle: "Best Card Payment Providers UK (2026) | CanDo Payments",
  metaDescription:
    "Compare UK card payment providers by business needs, pricing model and total cost. See what to check before choosing, with sources and a practical checklist.",
  // ISO dates. `reviewed` = date the provider information was checked (stated
  // in the copy). `published` = the real production launch date (5 Oct 2026,
  // release approved by Gary 2 Oct); never back-date it.
  reviewed: "2026-09-24",
  published: "2026-10-05",
  intro: [
    "The best card payment provider for a UK small business depends on how you take payments, the amount you process and the help you need. An occasional market stall, a busy café and an online shop can need very different setups.",
    "Start by comparing the total cost of taking payments over a typical month. Then check the equipment, software, payout arrangements and contract. A low transaction rate is useful only if the rest of the service works for you.",
  ],
  disclosure:
    "This guide is published by CanDo Payments, a merchant-services business included in the comparison. It is a six-provider starting shortlist, not an independent ranking or a review of every provider. The provider information below was checked against the linked UK pages on 24 September 2026. Suitability suggestions are our editorial judgement; we have not tested each provider's service.",
  sections: [
    {
      id: "providers",
      position: "provider_shortlist",
      heading: "Which providers should you compare?",
      blocks: [
        { type: "p", text: "The table is in alphabetical order. It covers published plans and tailored quotes, with options for in-person payments, point-of-sale software and online checkout. There is no overall winner." },
        {
          type: "table",
          caption: "Card payment providers to compare, in alphabetical order",
          columns: ["Provider", "A reason to include it on your shortlist", "What to compare before choosing"],
          rowHeaders: true,
          rows: [
            ["CanDo Payments", "You want to discuss your current payment setup and obtain a tailored comparison", "The complete written quote, equipment and software requirements, support arrangements and agreement terms"],
            ["Dojo", "You are comparing in-person payment equipment and associated business tools", "Device purchase versus monthly cost, processing charges, software plan and required integrations"],
            ["Square", "You want to assess payments alongside point-of-sale software", "Free versus paid software, hardware, transaction charges and any eligibility for custom pricing"],
            ["Stripe", "Your requirements include online checkout or a payment integration", "Payment-method and card-type fees, extra products, integration work and ongoing maintenance"],
            ["SumUp", "You want to compare pay-as-you-go, subscription and tailored pricing options", "Which plan fits your card sales, hardware needs and the eligibility and conditions of each offer"],
            ["Worldpay", "You want to obtain a tailored payment-processing proposal", "The quoted rates, device and service charges, settlement conditions and contract terms"],
          ],
        },
        { type: "h3", text: "CanDo Payments: compare your current setup" },
        { type: "p", text: "CanDo offers a statement-review service that compares your existing processing costs with a proposed CanDo setup. It can be a useful starting point if your [current bill is difficult to understand](/hidden-fees-checker) or you want help assessing a change." },
        { type: "p", text: "Ask us to explain the processing, equipment and software costs separately. Check the proposed service against the same requirements you give other providers. Compare a tailored quote with published plans using the same sales, transaction count and equipment needs. It is not automatically cheaper." },
        { type: "p", text: "[Request a statement review](/statement-review)." },
        { type: "h3", text: "Dojo: compare the equipment and plan together" },
        { type: "p", text: "Dojo's UK pricing page sets out hardware purchase and monthly options alongside software plans. Ask for a proposal covering the devices and tools your business would use. If you already have a till system, confirm compatibility before deciding." },
        { type: "p", text: "[Check Dojo's current pricing](https://dojo.tech/pricing/)." },
        { type: "h3", text: "Square: look at the complete software setup" },
        { type: "p", text: "Square offers a free software tier, paid options and custom pricing subject to eligibility. Include the features you need in the comparison rather than assuming the free plan covers your whole operation. Hardware and additional software can change the total." },
        { type: "p", text: "[Check Square's UK plans](https://squareup.com/gb/en/pricing)." },
        { type: "h3", text: "Stripe: understand the work behind the checkout" },
        { type: "p", text: "Stripe offers payment products for online and in-person use, with standard and custom pricing. For an online shop, check the payment methods, card mix and extra products you need. Include the cost of setting up and maintaining your chosen integration." },
        { type: "p", text: "[Check Stripe's UK pricing](https://stripe.com/gb/pricing)." },
        { type: "h3", text: "SumUp: compare all the relevant plans" },
        { type: "p", text: "SumUp offers pay-as-you-go, Payments Plus and tailored pricing. Its entry-level transaction rate is therefore only one option. Work through the plan that applies to your business, including the subscription, equipment and relevant card conditions." },
        { type: "p", text: "[Check SumUp's UK pricing](https://www.sumup.com/en-gb/pricing/)." },
        { type: "h3", text: "Worldpay: use the written proposal" },
        { type: "p", text: "Worldpay advertises tailored pricing for small businesses. Obtain a proposal for your own payment mix and equipment needs, then compare it with the other offers on the same basis. A tailored offer needs enough detail to calculate the full cost." },
        { type: "p", text: "[Explore Worldpay's small-business options](https://www.worldpay.com/en-GB/small-business)." },
      ],
    },
    {
      id: "monthly-bill",
      position: "cost_comparison",
      heading: "Compare the monthly bill, not just the percentage",
      blocks: [
        { type: "p", text: "For each offer, add together:" },
        {
          type: "ul",
          items: [
            "Percentage charges for the card types and payment channels you use.",
            "Fixed charges per transaction, where applicable.",
            "Terminal, software and other recurring charges.",
            "Any minimum charges and usage-based extras.",
          ],
        },
        { type: "p", text: "Then consider setup costs and the cost of leaving your current agreements. Ask which charges include VAT and which do not." },
        { type: "p", text: "Here is a simplified example using invented offers. These are not provider prices or a CanDo quotation. Assume both offers cover the same transactions, with no other charges." },
        {
          type: "table",
          caption: "Worked example with invented offers",
          columns: ["Monthly card sales", "Offer A: 1.5%, no monthly fee", "Offer B: 1.0% plus £20 monthly"],
          align: ["left", "right", "right"],
          rowHeaders: true,
          rows: [
            ["£2,000", "£30", "£40"],
            ["£4,000", "£60", "£60"],
            ["£10,000", "£150", "£120"],
          ],
        },
        { type: "p", text: "In this example, the offers cost the same at £4,000 a month. The lower percentage becomes cheaper above that point because it offsets the monthly fee. Add a terminal charge or a different card mix and the answer can change." },
        { type: "p", text: "Transaction size matters too. At £10,000 of monthly card sales, an average sale of £10 means 1,000 transactions. A hypothetical 5p charge on each would add £50. At an average sale of £50, the same card sales mean 200 transactions and £10 in those charges." },
        { type: "p", text: "For a seasonal business, compare a quiet month and a busy month as well as the annual total." },
      ],
    },
    {
      id: "working-day",
      position: "workflow",
      heading: "Check how the service fits your working day",
      blocks: [
        { type: "p", text: "**For a [café, pub or restaurant](/industries/hospitality):** write down where staff take orders and payments, whether you need to connect to an existing [electronic point-of-sale (EPOS) system](/epos-systems), and how bills, refunds and tips are handled. Ask for a demonstration of the workflow you will use." },
        { type: "p", text: "**For a [retail shop](/industries/retail):** consider your checkout, stock system, receipts and returns. If you sell online too, establish which information needs to move between the shop and website." },
        { type: "p", text: "**For an online business:** check the integration, payment methods, recurring-payment requirements and who will maintain the connection. Our [online payments page](/online-payments) is a starting point for discussing those requirements with CanDo." },
        { type: "p", text: "Before signing, ask when funds become available, what can delay them and how support works during your trading hours. Get the applicable conditions in writing." },
      ],
    },
    {
      id: "before-signing",
      position: "before_signing",
      heading: "Five questions to ask before signing",
      blocks: [
        {
          type: "ol",
          items: [
            "What would the full bill be for my recent payment mix, including equipment and software?",
            "Which charges or conditions change for different cards, refunds, disputes or payment channels?",
            "What is the agreement length, notice period and cost of leaving?",
            "Does the proposed setup work with my existing software, and who handles installation and support?",
            "What happens if a terminal or connection fails during service?",
          ],
        },
        { type: "p", text: "Keep your existing service until you have agreed a workable transition. Include any remaining obligations when [weighing up a switch](/switch-vs-stay)." },
      ],
    },
    {
      id: "common-questions",
      position: "faq",
      heading: "Common questions",
      blocks: [
        { type: "h3", text: "Who is the cheapest card payment provider for a small business?" },
        { type: "p", text: "A headline rate alone cannot tell you which provider is cheapest. Compare the total charge for the same card sales, transaction count, card mix, equipment and software. A provider with a lower percentage can still cost more once fixed charges are included." },
        { type: "h3", text: "Should I change provider when I reach £10,000 a month?" },
        { type: "p", text: "There is no universal turnover point at which you should switch. At £10,000 a month, review the options against your actual payment mix. Ask your existing provider about its relevant plans and compare written alternatives. Include switching costs and any features you would lose." },
        { type: "h3", text: "Do I need to replace my till to change payment provider?" },
        { type: "p", text: "Check the compatibility of the exact till, software version and payment setup. Ask what an integrated connection supports and what happens if you use a [separate card machine](/card-machines). Do not assume that a provider logo on a compatibility list covers every configuration." },
        { type: "h3", text: "Can CanDo help me compare my current charges?" },
        { type: "p", text: "Yes. You can request a review of a recent processing statement and receive a comparison with a proposed CanDo setup. Any decision should take account of the complete offer and your existing obligations." },
      ],
    },
  ],
  closing: {
    id: "next-step",
    position: "closing",
    heading: "Put your current costs beside a written alternative",
    body: "If you already take card payments, a recent statement gives the comparison a practical starting point. Tell us what works in your current setup and what you want to improve.",
    primary: { label: "Request a statement review", to: "/statement-review" },
    secondary: "Starting a business or choosing a setup for the first time? **[Explore the payment solution finder](/payment-finder)**.",
  },
};

const CARD_MACHINE_FEES = {
  slug: "card-machine-fees",
  guideId: "card-machine-fees",
  path: "/guides/card-machine-fees",
  eyebrow: "Comparison guide",
  h1: "Card machine fees for UK small businesses: compare the total cost",
  // Approved metadata: PAGE-METADATA.json (AI-Search-Guides-20260924-v03).
  seoTitle: "Card Machine Fees for UK Small Businesses | CanDo",
  metaDescription:
    "Compare card machine fees for UK small businesses. Understand processing charges, monthly costs and rental with clear worked examples and a quote checklist.",
  reviewed: "2026-09-24",
  published: "2026-10-05",
  intro: [
    "For a UK small business, card machine fees can include processing charges, equipment costs and ongoing service fees. Add up the full bill before comparing offers, then check what each agreement includes.",
    "Start with what your business actually takes in card payments. Two businesses processing £10,000 a month can have different costs if one takes hundreds of small payments and the other takes fewer, larger payments.",
  ],
  disclosure:
    "Use your statement and written quotes alongside this guide. All worked examples are hypothetical and show how the calculations work. They are not current provider prices, a CanDo quotation or a prediction of what you will save.",
  sections: [
    {
      id: "bill-charges",
      position: "bill_charges",
      heading: "What can appear on a card payment bill?",
      blocks: [
        { type: "p", text: "Charges vary by provider, plan and payment setup. Some are bundled into one price; others appear separately. Use this list to check a quote, rather than assuming every charge applies." },
        {
          type: "table",
          caption: "Charges that can appear on a card payment bill",
          columns: ["Charge", "What to check"],
          rowHeaders: true,
          rows: [
            ["Percentage processing charge", "Which rate applies to each card type and payment channel? Is the quoted rate the complete processing charge or one component?"],
            ["Fixed transaction or authorisation charge", "How much is charged per item, and which events count — successful payments, attempts or other activity?"],
            ["Card machine purchase or rental", "What is the cost for the number of devices you need? Check the equipment agreement separately."],
            ["Software, gateway or service subscription", "Which features and connections are included, and which have an extra charge?"],
            ["Minimum monthly charge", "Is there a minimum, what counts towards it, and how is any shortfall calculated?"],
            ["PCI-related charges", "What service or requirement does each charge relate to? Distinguish a regular programme fee from a non-compliance charge."],
            ["Refund, dispute or retrieval charges", "What happens to the original processing fee, and are there additional charges when an issue occurs?"],
            ["Setup, changes and leaving", "Ask about installation, additional devices, cancellation, notice periods and equipment return."],
          ],
        },
        { type: "p", text: "A statement may group these under headings such as card fees, activity fees and other fees. Elavon's [statement guide](https://www.elavon.co.uk/content/dam/elavon/en-gb/documents/customer-centre/setting-up-your-account/Understanding_Your_Statement_vs2_UK.pdf) illustrates these categories; its sample amounts are not a current price list." },
        { type: "p", text: "If you are also changing your [card machines](/card-machines), compare the equipment and payment service together. Buying a device and renting one create different upfront and ongoing costs." },
      ],
    },
    {
      id: "processing-rate",
      position: "processing_rate",
      heading: "Is the headline rate the whole processing charge?",
      blocks: [
        { type: "p", text: "Ask the provider to show how its price is built up. A quoted rate can apply only to specified card types or channels; ask which payments are included. Another quote may show underlying costs and the provider's charge separately." },
        { type: "p", text: "The Payment Systems Regulator explains that the merchant service charge has several components and usually includes interchange. An interchange rate on its own is not the price a business pays for the complete payment service. [Read the PSR's explanation](https://www.psr.org.uk/our-work/card-payments/the-ifr-and-consumers/)." },
        { type: "p", text: "For each quote, ask for the total processing cost using the same card mix. Include any business, international or other card categories you accept, and separate in-person payments from online or telephone payments where their prices differ." },
        { type: "p", text: "Do not add a fee twice. If a quoted rate already includes a component, adding it again will make the comparison wrong." },
      ],
    },
    {
      id: "monthly-cost",
      position: "monthly_cost",
      heading: "How much should card processing cost a UK small business?",
      blocks: [
        { type: "p", text: "There is no single monthly figure that suits every business. Work out the total for your card sales, payment count and card mix, including equipment and other applicable charges. Then compare written quotes using those same figures. The invented example below totals £137 for £10,000 in monthly card sales; it illustrates the calculation, not a typical UK price." },
        { type: "h3", text: "Work out your monthly cost" },
        { type: "p", text: "For a straightforward quote with a percentage, a fixed fee per successful payment and a monthly charge, use:" },
        { type: "p", text: "**Monthly cost = card sales × percentage rate + number of payments × fixed fee + monthly charges.**" },
        { type: "p", text: "Add any other applicable costs. Where different rates apply, calculate each group separately and add the results. An authorisation fee may use a different count from successful sales, so follow the provider's actual charging rules." },
        { type: "p", text: "Here is an invented month with £10,000 in card sales across 400 successful payments. Assume one rate covers all the payments, there are no other chargeable attempts or activities, and no additional costs or VAT are included in this illustration." },
        {
          type: "table",
          caption: "Invented example: one month's included costs",
          columns: ["Cost item", "Calculation", "Amount"],
          align: ["left", "left", "right"],
          rowHeaders: true,
          rows: [
            ["Processing percentage", "£10,000 × 1.0%", "£100.00"],
            ["Fixed payment fee", "400 × £0.03", "£12.00"],
            ["Terminal rental", "One terminal", "£20.00"],
            ["Service fee", "Monthly charge", "£5.00"],
            ["**Total included monthly cost**", "", "**£137.00**"],
          ],
        },
        { type: "p", text: "The processing percentage used in this illustration is 1.0%. The included monthly costs together equal **1.37% of card sales**: £137 ÷ £10,000 × 100." },
        { type: "p", text: "That second figure is a useful comparison measure, provided you include the same costs and use the same sales basis for every offer. It is not a new contractual rate. Keep setup charges and unusual one-off costs visible rather than hiding them inside an apparently normal month." },
        { type: "p", text: "For a real comparison, confirm which charges include VAT and show it consistently. Do not assume every line has the same treatment." },
      ],
    },
    {
      id: "cheapest",
      position: "cheapest",
      heading: "What is the cheapest way to take card payments in the UK?",
      blocks: [
        { type: "p", text: "Compare the full cost of suitable offers for the way your business takes payments. A plan without a monthly fee can cost less at one sales volume, while a plan with lower transaction charges can cost less at another. Check quiet months, average payment size and the service included before choosing." },
        { type: "h3", text: "Compare two offers using the same business figures" },
        { type: "p", text: "Consider two invented offers:" },
        {
          type: "ul",
          items: [
            "**Offer A:** 1.5% per successful payment, with no fixed transaction or monthly charge.",
            "**Offer B:** 1.0% plus 3p per successful payment, with £25 in monthly charges.",
          ],
        },
        { type: "p", text: "Assume an average payment of £25, the same payment mix and equivalent equipment and service. There are no other fees, refunds, disputes, setup costs or VAT in this illustration." },
        {
          type: "table",
          caption: "Invented example: Offer A and Offer B at different monthly card sales",
          columns: ["Monthly card sales", "Successful payments", "Offer A", "Offer B"],
          align: ["left", "right", "right", "right"],
          rowHeaders: true,
          rows: [
            ["£2,000", "80", "£30.00", "£47.40"],
            ["£5,000", "200", "£75.00", "£81.00"],
            ["£10,000", "400", "£150.00", "£137.00"],
            ["£20,000", "800", "£300.00", "£249.00"],
          ],
        },
        { type: "p", text: "Offer A costs less in the two quieter months shown. Offer B costs less at the higher volumes because the reduction in percentage charges outweighs its fixed costs." },
        { type: "p", text: "Now change the average payment to £10. At £10,000 in monthly card sales, there would be 1,000 payments. Offer B would cost **£155**, while Offer A would still cost **£150**. The lower percentage no longer gives the lower total in this example." },
        { type: "p", text: "A café taking many small payments and a shop taking fewer, larger payments can reach the same monthly card sales with different fixed transaction costs." },
      ],
    },
    {
      id: "quiet-months",
      position: "quiet_months",
      heading: "Allow for quiet months and switching costs",
      blocks: [
        { type: "p", text: "For a seasonal business, repeat the calculation for each month. A plan that looks attractive during peak trading may cost more across the year." },
        { type: "p", text: "Using the £25 average-payment example above, suppose the business has three months at £20,000 and nine months at £2,000. Offer A would total **£1,170** for the year; Offer B would total **£1,173.60**. Their annual costs are almost the same, despite Offer B looking cheaper in a busy month." },
        { type: "p", text: "Then add the costs of getting started or switching. Put these on separate lines:" },
        {
          type: "ul",
          items: [
            "New equipment, installation or integration work.",
            "Any remaining charges under your existing agreements.",
            "Any period when you expect to pay for both services.",
          ],
        },
        { type: "p", text: "Check the processing and equipment agreements separately, including notice periods and return requirements. Compare the first-year total as well as the ongoing monthly bill. Make sure the service, equipment and payout timing work for you." },
      ],
    },
    {
      id: "quote-preparation",
      position: "quote_preparation",
      heading: "Gather the right figures before asking for a quote",
      blocks: [
        { type: "p", text: "Use a recent processing statement, plus any separate equipment or software invoices. If trading varies, include a quiet period and a busy one." },
        { type: "p", text: "Collect the same information for every provider:" },
        {
          type: "ol",
          items: [
            "Card sales and transaction count for the period being compared.",
            "Available card-type and payment-channel breakdowns.",
            "Every recurring payment-related charge, including separate invoices.",
            "Any refunds, disputes or unusual fees that need explaining.",
            "The equipment and software you need, plus current agreement dates.",
          ],
        },
        { type: "p", text: "Use card sales for the comparison, not total business turnover that includes cash or bank transfers. Check what the statement's sales figure includes and how refunds are presented. A bank payout can differ from sales, so it should not automatically become your comparison figure." },
        { type: "p", text: "Ask each provider to price that same profile and list anything it has left out. Where available, use its pricing summary box alongside the full quote and terms. The PSR provides [guidance on summary boxes and quotation tools](https://www.psr.org.uk/publications/market-reviews/implementation-advice-for-card-acquiring-market-remedies-summary-box-and-online-quotation-tool-templates/) to support clearer comparisons." },
      ],
    },
    {
      id: "unrecognised-fee",
      position: "unrecognised_fee",
      heading: "What to do about a fee you do not recognise",
      blocks: [
        { type: "p", text: "Ask what triggers the charge, what it covers and where it appears in your agreement. An unfamiliar name does not establish that a fee is wrong, avoidable or charged by every provider." },
        { type: "p", text: "With a PCI-related fee, ask whether it pays for an ongoing service or relates to an outstanding compliance requirement. The PCI Security Standards Council says it does not run compliance programmes or impose consequences for non-compliance. Ask your provider to explain the requirements and charges that apply to your account. [PCI SSC guidance](https://www.pcisecuritystandards.org/faqs/1015/)." },
        { type: "p", text: "You can use CanDo's [hidden-fees checker](/hidden-fees-checker) as an initial check of the figures you enter. Its output is illustrative and uses general assumptions; it is not a formal audit or a guarantee of savings." },
      ],
    },
    {
      id: "common-questions",
      position: "faq",
      heading: "Common questions",
      blocks: [
        { type: "h3", text: "Can I get a card machine without a monthly fee?" },
        { type: "p", text: "Some plans have no monthly processing-plan charge. That does not make taking payments free: check hardware costs, transaction fees and any optional services. SumUp, for example, currently offers both pay-as-you-go and paid-plan options. Compare the complete setup you would use. [See SumUp's current UK pricing](https://www.sumup.com/en-gb/pricing/)." },
        { type: "h3", text: "Will a lower transaction percentage save me money?" },
        { type: "p", text: "Only if the full cost is lower for your payment profile. A monthly fee or per-payment charge can outweigh the difference. Use the examples above to check how your own volume and average payment size affect the calculation." },
        { type: "h3", text: "Do I get the processing fee back when I refund a customer?" },
        { type: "p", text: "Check the applicable terms. Ask separately whether the original processing fee is returned and whether making the refund incurs another charge. Include the expected effect of refunds when comparing offers; do not assume they all handle them in the same way." },
        { type: "h3", text: "Does a PCI fee mean everything is taken care of?" },
        { type: "p", text: "Do not treat a charge on your statement as proof that you have completed your requirements. Ask your provider what the fee covers, what you still need to do and how your status is confirmed." },
      ],
    },
  ],
  closing: {
    id: "next-step",
    position: "closing",
    heading: "Put your statement beside a written alternative",
    body: [
      "If you already take card payments, CanDo can review a recent processing statement and provide a comparison with a proposed CanDo setup.",
      "Use that comparison to check the full cost and the service you need. Any decision should include your existing obligations and the complete written offer.",
    ],
    primary: { label: "Request a statement review", to: "/statement-review" },
    secondary: "Choosing your first setup? **[Explore the payment solution finder](/payment-finder)**.",
    footnote: "Published by CanDo Payments. Source information checked 24 September 2026. Worked examples are illustrative, not provider quotations.",
  },
};

// Index order: provider guide first (proposed production order), then fees.
export const GUIDES = [CARD_PAYMENT_PROVIDERS_UK, CARD_MACHINE_FEES];

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug) || null;
}

// ─── Controlled tracking values (comparison-guides-v1) ──────────────────
// guide_link_click is only emitted when all three values are in these
// sets; anything else fails closed (no event, no fallback). GTM should
// apply the same allow-lists as its validation boundary.
export const GUIDE_IDS = GUIDES.map((g) => g.guideId);
export const GUIDE_LINK_ROLES = ["statement_review", "payment_finder", "internal_resource", "provider_source"];
export const GUIDE_CTA_POSITIONS = [
  ...new Set(GUIDES.flatMap((g) => [...g.sections.map((s) => s.position), g.closing.position])),
];
export const GUIDE_CONTENT_GROUP = "comparison_guides";

/** guideId for a /guides/{slug} path, or "" for any other route. */
export function guideIdForPath(pathname) {
  const m = String(pathname || "").match(/^\/guides\/([^/]+)\/?$/);
  return (m && getGuide(m[1])?.guideId) || "";
}

/** Strip link/bold markup — for meta text and schema. */
export function guidePlainText(copy) {
  return String(copy || "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1");
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-09-24" → "24 September 2026" (deterministic: same in prerender and browser). */
export function formatGuideDate(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MONTHS[m - 1]} ${y}`;
}
